<script setup lang="ts">
import { useDebounceFn, useMouse } from '@vueuse/core'
import VoixColorPalette from '@voix/components/fields/color/Palette.vue'
import { computed, onMounted, reactive, ref, watch } from 'vue'

import { usePageStore } from '@voix/store/pageStore'
import { useLayoutStore } from '@voix/store/layoutStore'

import SectionLabel from '@voix/components/chrome/SectionLabel.vue'
import TextInput from './TextInput.vue'

const props = defineProps<{
  modelValue: string | null
  label: string
  where: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const localValue = computed({
  get: (): string => {
    return props.modelValue ? props.modelValue : ''
  },
  set(value: string) {
    state.hexValue = value
    emit('update:modelValue', value)
  },
})

const layoutStore = useLayoutStore()
const pageStore = usePageStore()

const layout = computed(() => {
  if (!layoutStore.layouts || !pageStore.currentPage)
    return null

  return layoutStore.layouts[pageStore.currentPage?.layout]
})

const showPicker = computed(() => {
  const colorsConfig = layout.value?.colors
  const pickerConfig = colorsConfig?.[props.where]

  if (pickerConfig) {
    const pickerConfigIsSet = Object.prototype.hasOwnProperty.call(pickerConfig, 'picker')
    if (pickerConfigIsSet)
      return layout.value?.colors?.[props.where].picker
  }

  return true
})

const showPalette = computed(() => {
  if (layout.value?.colorPalette)
    return layout.value?.colors[props.where]?.palette && Object.keys(layout.value?.colorPalette).length > 0

  return false
})

const canvas = ref<HTMLCanvasElement | null>(null)
const ctx = ref<CanvasRenderingContext2D | null>(null)
const width = 300
const height = 150

const state = reactive({
  isDragging: false,
  colorPosition: { x: 0, y: 0 },
  hexValue: '#ffffff',
})

function drawGradient() {
  if (!canvas.value || !ctx.value)
    return
  ctx.value.clearRect(0, 0, width, height)

  // Create horizontal color spectrum gradient
  const gradientX = ctx.value.createLinearGradient(0, 0, width, 0)
  gradientX.addColorStop(0, 'red')
  gradientX.addColorStop(1 / 7, 'orange')
  gradientX.addColorStop(2 / 7, 'yellow')
  gradientX.addColorStop(3 / 7, 'green')
  gradientX.addColorStop(4 / 7, 'cyan')
  gradientX.addColorStop(5 / 7, 'blue')
  gradientX.addColorStop(6 / 7, 'magenta')
  gradientX.addColorStop(1, 'red')
  ctx.value.fillStyle = gradientX
  ctx.value.fillRect(0, 0, width, height)

  // Create vertical white to black gradient
  const gradientY = ctx.value.createLinearGradient(0, 0, 0, height)
  gradientY.addColorStop(0, 'rgba(255, 255, 255, 1)')
  gradientY.addColorStop(0.05, 'rgba(255, 255, 255, 1)')
  gradientY.addColorStop(1 / 3, 'rgba(255, 255, 255, 0)')
  gradientY.addColorStop(2 / 3, 'rgba(0, 0, 0, 0)')
  gradientY.addColorStop(0.95, 'rgba(0, 0, 0, 1)')
  gradientY.addColorStop(1, 'rgba(0, 0, 0, 1)')
  ctx.value.fillStyle = gradientY
  ctx.value.fillRect(0, 0, width, height)
}

function updateColorPosition(x: number, y: number) {
  state.colorPosition.x = x + 15
  state.colorPosition.y = y + 15
  if (!ctx.value)
    return
  const imageData = ctx.value.getImageData(x, y, 1, 1).data
  state.hexValue = rgbToHex(imageData[0], imageData[1], imageData[2])
  emit('update:modelValue', state.hexValue)
}

function startDrag(event: MouseEvent) {
  state.isDragging = true
  updateColorPosition(event.offsetX, event.offsetY)
}

function onMouseMove(event: MouseEvent) {
  if (state.isDragging) {
    updateColorPosition(event.offsetX, event.offsetY)
  }
}

function stopDrag() {
  state.isDragging = false
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    `#${
      [r, g, b]
        .map((x) => {
          const hex = x.toString(16)
          return hex.length === 1 ? `0${hex}` : hex
        })
        .join('')}`
  )
}

function hexToRgb(hex: string): { r: number, g: number, b: number } | null {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b)
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: Number.parseInt(result[1], 16),
        g: Number.parseInt(result[2], 16),
        b: Number.parseInt(result[3], 16),
      }
    : null
}

function updateFromHex() {
  const rgb = hexToRgb(state.hexValue)
  if (!rgb || !ctx.value)
    return

  // Loop through the canvas to find the closest color
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const imageData = ctx.value.getImageData(x, y, 1, 1).data
      if (
        imageData[0] === rgb.r
        && imageData[1] === rgb.g
        && imageData[2] === rgb.b
      ) {
        state.colorPosition.x = x
        state.colorPosition.y = y
        return
      }
    }
  }
}

onMounted(() => {
  if (canvas.value) {
    ctx.value = canvas.value.getContext('2d')
    drawGradient()
  }
  if (props.modelValue)
    localValue.value = props.modelValue
})

const indicatorStyle = computed(() => ({
  left: `${state.colorPosition.x}px`,
  top: `${state.colorPosition.y}px`,
  position: 'absolute' as const,
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  border: '2px solid white',
  backgroundColor: state.hexValue,
  transform: 'translate(-50%, -50%)',
}))

const showPickerPopup = ref(false)
const colorPickerTemporaryValue = ref('')

const { x, y } = useMouse()
const savedMouseCoords = reactive({
  x: 0,
  y: 0,
})

function openColorPick() {
  // Save the current coordinates on the screen
  savedMouseCoords.x = x.value - 300
  savedMouseCoords.y = y.value

  if (props.modelValue)
    colorPickerTemporaryValue.value = props.modelValue
  showPickerPopup.value = true
}

function applyColorPick() {
  emit('update:modelValue', state.hexValue)
  showPickerPopup.value = false
}

function cancelColorPick() {
  emit('update:modelValue', colorPickerTemporaryValue.value)
  state.hexValue = colorPickerTemporaryValue.value
  showPickerPopup.value = false
}
</script>

<template>
  <div v-if="showPalette || showPicker">
    <SectionLabel v-if="props.label">
      {{ props.label }}
    </SectionLabel>
    <div class="relative flex space-x-2">
      <button
        class="relative w-6 h-6 rounded-full overflow-hidden"
        @click="openColorPick"
      >
        <div
          class="w-6 h-6 rounded-full border border-gray-300 pointer-events-none"
          :style="{ backgroundColor: props.modelValue }"
        />
        <div
          v-if="!props.modelValue"
          class="w-full border-b-4 border-red-600 rotate-45 -translate-y-3.5 pointer-events-none"
        />
      </button>

      <Teleport to="#voix-admin-root">
        <div
          v-show="showPickerPopup" class="color-picker text-xs" :style="{
            top: `${savedMouseCoords.y + 10}px`,
            left: `${savedMouseCoords.x}px`,
          }"
        >
          <div class="flex space-x-2">
            <div>
              <canvas ref="canvas" @mousedown="startDrag" @mousemove="onMouseMove" @mouseup="stopDrag" />
              <div
                class="indicator"
                :style="indicatorStyle"
              />
              <!-- <input v-model="state.hexValue" type="text" @input="updateFromHex"> -->
              <TextInput id="color-picker-hex-value" v-model="localValue" label="" @input="updateFromHex" />
            </div>
            <div class="space-y-2">
              <button class="voix-admin-bg-light voix-admin-text-dark transition-all duration-100 p-3 py-2 rounded-sm w-full" @click="applyColorPick">
                Apply
              </button>
              <button class="voix-admin-bg-light voix-admin-text-dark transition-all duration-100 p-3 py-2 rounded-sm w-full" @click="cancelColorPick">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Teleport>

      <button
        class="rounded-lg uppercase bg-gray-100 text-gray-400 text-xs font-bold px-2"
        @click="emit('update:modelValue', '')"
      >
        Clear
      </button>
    </div>
    <VoixColorPalette v-if="showPalette" v-model="localValue" :where="where" />
  </div>
</template>

<style scoped>
.color-picker{@apply fixed z-50 top-0 left-0 bg-white rounded overflow-hidden p-4;width:400px}canvas{@apply rounded overflow-hidden;cursor:crosshair;height:150px;width:300px}.indicator{pointer-events:none}
</style>
